import { setPageBuilderIsLoading } from "../../../../features/assetManagementSlice";
import {
  setDisplayErrorSnackBar,
  setIsLoading,
} from "../../../../features/userSlice";
import { createPageService } from "../../../../services/api/create-page.endpoints";
import store from "../../../../store";
import GenericEndPointsLogic from "../../../GenericEndPointsLogic/GenericEndPointsLogic";

// Static entity type sent to the variants endpoint for pages
const VARIANTS_ENTITY_TYPE = "page";

async function createPage(
  siteUid: string,
  data: any,
  setIsLoading: any,
  setCreatePageResponse: any,
  oauthTokenHeader: string,
  setShowErrorDialog: any
) {
  try {
    store.dispatch(setIsLoading(true));
    const response = await createPageService.createPage(
      data,
      siteUid,
      oauthTokenHeader
    );

    if (!response.status || response.status !== 200) {
      store.dispatch(setIsLoading(true));
      setShowErrorDialog(true); // Set error dialog to show
    } else {
      setCreatePageResponse(response?.data?.data);
      // Sync variants for the new page (non-blocking)
      GenericEndPointsLogic.updateVariants({
        oauthTokenHeader,
        websiteSharedUid: siteUid,
        entityType: VARIANTS_ENTITY_TYPE,
        entitySharedUid: response?.data?.data?.shared_uid,
        skipCacheTypeCheck: true, // Page Builder: no cache_type check
      });
    }
  } catch (e: any) {
    if (e.status !== 500) {
      store.dispatch(
        setDisplayErrorSnackBar({
          message: e?.parsedText || "Unknown error occurred",
          type: "error",
        })
      );
    }
    setShowErrorDialog(true); // Set error dialog to show
  } finally {
    store.dispatch(setIsLoading(false));
  }
}

async function duplicatePage(
  siteUid: string,
  data: any,
  dispatch: any,
  oauthTokenHeader: string,
  setDuplicatePageResponse: any,
  pageUid: string,
  setShowDuplicateErrorDialog?: any,
  setDuplicateErrorMessage?: any,
  skipGlobalLoaderOff?: boolean
) {
  try {
    dispatch(setPageBuilderIsLoading(true));
    store.dispatch(setIsLoading(true));
    const response = await createPageService.duplicatePage(
      data,
      siteUid,
      oauthTokenHeader,
      pageUid
    );

    if (!response.status || response.status !== 200) {
      const errorMessage = response?.data?.message || "Something went wrong while duplicating the page";
      if (setDuplicateErrorMessage) setDuplicateErrorMessage(errorMessage);
      if (response.status === 500) {
        if (setShowDuplicateErrorDialog) setShowDuplicateErrorDialog(true); // Show error dialog for duplicate page title
        store.dispatch(setIsLoading(false));
        return false; // Exit function to prevent further state updates
      }

      if (setShowDuplicateErrorDialog) setShowDuplicateErrorDialog(true); // Show error dialog for duplicate page title
      store.dispatch(setIsLoading(false));
      return false;
    } else {
      setDuplicatePageResponse(response?.data?.data);
      // Duplicate response holds the new page's shared_uid as the first value in `data`
      const duplicatedPageUid = Object.values(response?.data?.data ?? {})[0];
      if (typeof duplicatedPageUid === "string") {
        GenericEndPointsLogic.updateVariants({
          oauthTokenHeader,
          websiteSharedUid: siteUid,
          entityType: VARIANTS_ENTITY_TYPE,
          entitySharedUid: duplicatedPageUid,
          skipCacheTypeCheck: true, // Page Builder: no cache_type check
        });
      }
      store.dispatch(
        setDisplayErrorSnackBar({
          message: "Page Duplicated Successfully",
          type: "success",
        })
      );
      if (!skipGlobalLoaderOff) {
        store.dispatch(setIsLoading(false));
      }
      return true;
    }
  } catch (e: any) {
    const errorMsg = e?.parsedText || "Unknown error occurred";
    store.dispatch(
      setDisplayErrorSnackBar({
        message: errorMsg,
        type: "error",
      })
    );
    if (setDuplicateErrorMessage) setDuplicateErrorMessage(errorMsg);
    if (setShowDuplicateErrorDialog) setShowDuplicateErrorDialog(true);
    store.dispatch(setIsLoading(false));
    return false;
  } finally {
    dispatch(setPageBuilderIsLoading(false));
  }
}

async function getPageContentAfterCreate(
  siteUid: string,
  pageUid: string,
  oauthTokenHeader: string,
  setPageContentAfterCreate: any,
  setIsLoading: any
) {
  try {
    store.dispatch(setIsLoading(true));

    const response = await createPageService.getPageContent(
      siteUid,
      pageUid,
      oauthTokenHeader
    );

    if (
      !response.status ||
      response.status !== 200 ||
      response?.response?.status === 500
    ) {
      store.dispatch(
        setDisplayErrorSnackBar({
          message:
            "Something went wrong while Getting Page Content after create",
          type: "error",
        })
      );
    } else {
      setPageContentAfterCreate(response?.data?.data);
    }
  } catch (e: any) {
    store.dispatch(
      setDisplayErrorSnackBar({
        message: e?.parsedText || "Unknown error occurred",
        type: "error",
      })
    );
  } finally {
    store.dispatch(setIsLoading(false));
  }
}

// eslint-disable-next-line
export const createPageEndPointLogic = {
  createPage,
  getPageContentAfterCreate,
  duplicatePage,
};
